import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  Visibility,
  Share as ShareIcon,
  Person,
  ConfirmationNumber,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetEventByIdQuery,
  useIncrementEventShareMutation,
  useIncrementEventViewMutation,
} from "../../features/events/eventsApiSlice";
import {
  getCategoryConfig,
  formatEventDateRange,
  getEventStatusBadge,
} from "../../utils/eventHelpers";
import {
  DetailHero,
  DetailShareBar,
  DetailFloatingButton,
  DetailSkeleton,
  DetailErrorState,
  useDetailShare,
  useScrollEffect,
  DetailContentCard,
  EngagementStatsCard,
  DetailInfoField,
} from "../../components/Details";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";

const EventDetails: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareBar, setShowShareBar] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch event details
  const {
    data: event,
    isLoading,
    error,
  } = useGetEventByIdQuery(eventId || "", {
    skip: !eventId,
  });

  // Track share mutation
  const [incrementShare] = useIncrementEventShareMutation();

  // Track view mutation
  const [incrementView] = useIncrementEventViewMutation();

  // Share functionality with localStorage tracking
  const shareHandlers = useDetailShare({
    itemId: eventId || "",
    itemType: "event",
    shareText: event
      ? `🎉 Check out this amazing event!\n\n${event.title}\n${
          event.description
        }\n\n${formatEventDateRange(event.startDate, event.endDate)}\n📍 ${
          event.venue
        }\n\n`
      : "",
    incrementShareMutation: incrementShare,
  });

  // Increment view count once per user (localStorage tracking)
  useEffect(() => {
    if (event && eventId) {
      const viewKey = `event_viewed_${eventId}`;
      const hasViewed = localStorage.getItem(viewKey);

      if (!hasViewed) {
        // First time viewing - increment count and mark as viewed
        incrementView({ eventId, mallId: event.mallId });
        localStorage.setItem(viewKey, "true");
      }
    }
  }, [event, eventId, incrementView]);

  // Smooth scroll effect using custom hook
  useScrollEffect(contentRef, setShowShareBar);

  // Handle image carousel navigation
  const images = event?.galleryImages?.length
    ? event.galleryImages
    : event?.bannerImage
    ? [event.bannerImage]
    : [event?.thumbnailImage || "/placeholder-event.jpg"];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (error || !event) {
    return (
      <DetailErrorState onBack={() => navigate(-1)} message="Event not found" />
    );
  }

  const categoryConfig = getCategoryConfig(event.category);
  const statusBadge = getEventStatusBadge(
    event.startDate,
    event.endDate,
    event.status
  );

  // Build meta info for hero
  const heroMetaInfo = (
    <>
      {/* Date & Time */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: 1.5,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CalendarToday
            sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
          />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: { xs: "0.875rem", md: "0.938rem" },
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          {formatEventDateRange(event.startDate, event.endDate)}
        </Typography>
      </Box>

      {/* Location */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: 1.5,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LocationOn sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }} />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: { xs: "0.875rem", md: "0.938rem" },
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          {event.venue}
        </Typography>
      </Box>

      {/* View and Share Counts - Side by Side */}
      {(event.viewCount !== undefined || event.shareCount !== undefined) && (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2.5, md: 3 },
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Views */}
          {event.viewCount !== undefined && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Visibility
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {event.viewCount?.toLocaleString() || 0} views
              </Typography>
            </Box>
          )}

          {/* Shares */}
          {event.shareCount !== undefined && event.shareCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShareIcon
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {event.shareCount.toLocaleString()} shares
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        pb: 3,
      }}
    >
      {/* Hero Section - Using DetailHero component */}
      <DetailHero
        images={images}
        currentImageIndex={currentImageIndex}
        title={event.title}
        categoryLabel={categoryConfig.label}
        categoryIcon={categoryConfig.icon}
        gradientColors={categoryConfig.gradient}
        statusBadge={statusBadge}
        metaInfo={heroMetaInfo}
        onBack={() => navigate(-1)}
        onPrevImage={images.length > 1 ? handlePrevImage : undefined}
        onNextImage={images.length > 1 ? handleNextImage : undefined}
      />

      {/* Main Content - Slides up over hero */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 10,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 20, md: 32 },
          borderTopRightRadius: { xs: 20, md: 32 },
          boxShadow: "0 -8px 32px rgba(0,0,0,0.12)",
          willChange: "transform",
          mt: { xs: -2, md: -3 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 3, md: 4 }, pb: 2, px: { xs: 2, sm: 3 } }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {/* Description Section */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 700, mb: 2 }}
                >
                  About This Event
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.8,
                    fontSize: "1rem",
                  }}
                >
                  {event.description}
                </Typography>
              </CardContent>
            </Card>

            {/* Event Details Grid */}
            <Grid container spacing={3}>
              {/* Entry Information */}
              <Grid item xs={12} md={6}>
                <DetailContentCard
                  title="Entry Details"
                  icon={<ConfirmationNumber sx={{ color: "white" }} />}
                  iconBgColor="primary.main"
                  sx={{ height: "100%" }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <DetailInfoField
                      label="Entry Type"
                      value={
                        event.entryType === "free"
                          ? "Free Entry"
                          : event.entryType === "paid"
                          ? `Paid Entry - ₹${event.ticketPrice}`
                          : "Registration Required"
                      }
                    />
                    {event.ticketLink && (
                      <Button
                        variant="contained"
                        fullWidth
                        href={event.ticketLink}
                        target="_blank"
                        sx={{
                          background: categoryConfig.gradient,
                          color: "white",
                          fontWeight: 600,
                          py: 1.5,
                          borderRadius: 2,
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                          },
                        }}
                      >
                        Get Tickets
                      </Button>
                    )}
                  </Box>
                </DetailContentCard>
              </Grid>

              {/* Engagement Stats - Views & Shares */}
              <Grid item xs={12}>
                <EngagementStatsCard
                  viewCount={event.viewCount}
                  shareCount={event.shareCount}
                />
              </Grid>

              {/* Performers */}
              {event.performers && event.performers.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "secondary.main",
                            borderRadius: 2,
                            p: 1.5,
                            display: "flex",
                          }}
                        >
                          <Person sx={{ color: "white" }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Featured Performers
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {event.performers.map((performer, index) => (
                          <Chip
                            key={index}
                            label={performer}
                            sx={{
                              background:
                                "linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Highlights */}
              {event.highlights && event.highlights.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                      >
                        Event Highlights
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box
                        component="ul"
                        sx={{
                          pl: 2,
                          m: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {event.highlights.map((highlight, index) => (
                          <Box
                            component="li"
                            key={index}
                            sx={{
                              fontSize: "1rem",
                              color: "text.secondary",
                              lineHeight: 1.8,
                            }}
                          >
                            {highlight}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Rules */}
              {event.rules && event.rules.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                      >
                        Rules & Guidelines
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box
                        component="ul"
                        sx={{
                          pl: 2,
                          m: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {event.rules.map((rule, index) => (
                          <Box
                            component="li"
                            key={index}
                            sx={{
                              fontSize: "1rem",
                              color: "text.secondary",
                              lineHeight: 1.8,
                            }}
                          >
                            {rule}
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Contact Info */}
              {event.contactInfo && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                      >
                        Contact Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "text.secondary", lineHeight: 1.8 }}
                      >
                        {event.contactInfo}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Bottom Sticky Share Bar */}
      <DetailShareBar
        show={showShareBar}
        onWhatsAppShare={shareHandlers.handleWhatsAppShare}
        onCopyLink={shareHandlers.handleCopyLink}
        onClose={() => setShowShareBar(false)}
      />

      {/* Floating WhatsApp Button */}
      <DetailFloatingButton
        show={!showShareBar}
        onClick={shareHandlers.handleWhatsAppShare}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={shareHandlers.snackbar.open}
        autoHideDuration={3000}
        onClose={shareHandlers.handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={shareHandlers.handleSnackbarClose}
          severity={shareHandlers.snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontWeight: 600,
          }}
        >
          {shareHandlers.snackbar.message}
        </Alert>
      </Snackbar>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default EventDetails;
